import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { getIn } from 'formik'
import { useTranslation } from 'react-i18next'

import ErrorMessage from '../../../components/Form/ErrorMessage'
import FileUploadInput from '../../../components/Form/FileUploadInput'
import DropFileUploader from '../../../components/Form/DropFileUploader'
import FileDetailsPreview from '../../../components/FileDetailsPreview'

import useFileUpload from '../../../features/hooks/useFileUpload'
import { useVideoProcessing } from './hooks/useMediaUpload'

import { showToasts } from '../../../helpers/toasts'
import { getImageValues, getVideoValues } from './helpers'
import { getFileError } from '../../Facebook/AdForms/helpers'
import { getFileFormat, getFileType } from '../../../features/helpers/other'

import { TOAST_TYPE } from '../../../constants/other'
import { FILE_IS_PROCESSING } from '../../../constants/ads'
import { AD_FILE, FILE_NAME, FILE_TYPE, MEDIA_TYPE, UPLOADED_MEDIA_TYPE } from './fields'
import { FACEBOOK_PLATFORM } from '../../../constants/selectLists/platformList'

const defaultFileValue = {}
function AdFileUpload({
  values,
  fileValidationSchema,
  validateForm,
  error,
  onFileStartUpload,
  onFileUploaded,
  onFileRemove,
  mediaType,
  fileKey,
  fileNameKey,
  fileURLKey,
  fileTypeKey,
  uploadedMediaTypeKey,
  recommendedSize,
  accept,
  multiple = false,
  maxSize,
  isDraggable,
  platform
}) {
  const { t } = useTranslation()

  const selectedFile = getIn(values, fileKey) || defaultFileValue
  // file name should be taken from Formik to make sure that yup fileValidationSchema was made over the file
  const fileName = getIn(values, fileNameKey)
  const fileUrl = getIn(values, fileURLKey)
  const fileType = getIn(values, fileTypeKey)
  const uploadedMediaType = getIn(values, uploadedMediaTypeKey)

  const hasVideoProcessing = useMemo(() => {
    return platform === FACEBOOK_PLATFORM && fileType === 'video'
  }, [platform, fileType])

  const { uploadedFileData, isFileLoading, fileUploadProgress, fileIsUploaded, handleRemoveFile, allowFileDelete } =
    useFileUpload({
      fileName,
      fileUrl,
      onFileRemove,
      // Do not pass onFileUploaded if file should be processed, because it will be called in useVideoProcessing hook
      ...(!hasVideoProcessing && { onFileUploaded })
    })

  useVideoProcessing({
    uploadedFileData,
    ...(hasVideoProcessing && {
      onFileUploaded
    })
  })

  const filePreviewType = useMemo(() => selectedFile.type && getFileType(selectedFile), [selectedFile])
  const filePreview = useMemo(
    () => (selectedFile.name && URL.createObjectURL(selectedFile)) || fileUrl,
    [selectedFile, fileUrl]
  )
  const showPreview = (isFileLoading || fileIsUploaded || fileUrl) && uploadedMediaType === mediaType

  const isFileProcessing = uploadedFileData && uploadedFileData.status === FILE_IS_PROCESSING

  const validateFile = async newFileValues => {
    try {
      // use either separate schema for validation or formik validateForm method
      // todo refactor to use fileValidationSchema instead of full form validation and keeping validation fields in formik
      if (fileValidationSchema) {
        return fileValidationSchema.validate(newFileValues).catch(error => {
          const errorMessage = error?.message
          throw new Error(errorMessage)
        })
      } else {
        return validateForm({ ...values, ...newFileValues }).then(errors => {
          const error = getFileError(errors)
          if (error) {
            throw new Error(error)
          }
        })
      }
    } catch (error) {
      throw error
    }
  }

  async function loadFiles(files) {
    const newFiles = []

    for (const file of files) {
      // Get file properties
      const url = URL.createObjectURL(file)
      const size = file.size
      const fileName = file.name
      const fileType = getFileType(file)
      const fileFormat = getFileFormat(file)

      // Create object with file properties
      const fileValues = {
        [AD_FILE]: file,
        [FILE_NAME]: fileName,
        [MEDIA_TYPE]: fileType,
        [FILE_TYPE]: fileFormat,
        [UPLOADED_MEDIA_TYPE]: mediaType
      }

      try {
        // Get image or video values and validate them
        let mediaValues = {}
        if (fileType === 'image') {
          const imageValues = await getImageValues({ url, size, fileFormat })
          mediaValues = { ...fileValues, ...imageValues }
        } else {
          const videoValues = await getVideoValues({ url, size, fileFormat })
          mediaValues = { ...fileValues, ...videoValues }
        }

        // Validate media values and add to newFiles array
        await validateFile(mediaValues)
        newFiles.push(mediaValues)
      } catch (error) {
        const errorMessage = error?.message || error

        // Show error toast and skip file
        showToasts({
          type: TOAST_TYPE.error,
          message: t(errorMessage)
        })
      }
    }

    return newFiles
  }

  async function handleFilesChange(files) {
    const newFiles = await loadFiles(multiple ? files : [files])

    // files could be skipped during validation so the check is required to avoid empty array
    if (newFiles.length) {
      onFileStartUpload(files, multiple ? newFiles : newFiles[0])
    }
  }

  return (
    <>
      {showPreview ? (
        <FileDetailsPreview
          fileName={fileName}
          // represent preview from fileUrl if filePreview is empty - for example when data is preloaded form saved data in session storage
          fileUrl={filePreview}
          filePreviewType={filePreviewType || fileType}
          removeFile={handleRemoveFile}
          isFileLoading={isFileLoading}
          allowDelete={allowFileDelete}
          fileUploadProgress={fileUploadProgress}
        />
      ) : (
        <>
          {recommendedSize && <p>{t('Recommended size') + ': ' + recommendedSize}</p>}
          {isDraggable ? (
            <DropFileUploader
              accept={accept}
              uploadText="Drag and drop your file, or click here"
              onUpload={handleFilesChange}
              multiple={multiple}
            />
          ) : (
            <FileUploadInput
              name={AD_FILE}
              onFileUpload={handleFilesChange}
              accept={accept}
              maxSize={maxSize}
              multiple={multiple}
            />
          )}
        </>
      )}

      {error && !isFileLoading && (
        <ErrorMessage error={isFileProcessing ? 'Please wait while the video file is processing' : error} />
      )}
    </>
  )
}

AdFileUpload.propTypes = {
  values: PropTypes.object.isRequired,
  validateForm: PropTypes.func,
  fileValidationSchema: PropTypes.object,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  onFileStartUpload: PropTypes.func.isRequired,
  onFileUploaded: PropTypes.func.isRequired,
  onFileRemove: PropTypes.func,
  fileKey: PropTypes.string.isRequired,
  fileNameKey: PropTypes.string,
  fileURLKey: PropTypes.string,
  fileTypeKey: PropTypes.string,
  recommendedSize: PropTypes.string,
  maxSize: PropTypes.number,
  isDraggable: PropTypes.bool
}

export default AdFileUpload
